<template>
  <div
    class="d-widget-shortcut"
    :class="{'fts-cursor-pointer': !(isCreate || isError)}"
    :data-id="item.id"
    :data-widget-load="loading ? 'loading' : 'load'"
    @click="!isCreate && !isError && routeListing()"
    @mouseenter="showAction = true"
    @mouseleave="showAction = false"
  >
    <!-- overlay -->
    <div class="d-widget-shortcut-overlay">
      <fts-moji :emoji="overlayEmoji" :size="96" class="d-widget-shortcut-overlay-emoji" v-if="overlayEmoji" />
    </div>
    <!-- header -->
    <div class="d-widget-shortcut-header">
      <fts-widget-title
        :modules="modules"
        :module-id="item.moduleId"
        :listing-id="item.listingId"
        :widget-name="item.name"
      />
    </div>
    <!-- footer -->
    <div class="d-widget-shortcut-footer">
      <!-- create -->
      <div class="d-widget-shortcut-new" v-if="isCreate">
        <fts-btn
          color="primary"
          text
          text-transform-init
          @click="entryCreate"   
        >
          <font-awesome-icon :icon="['fas', 'plus']" /> New
        </fts-btn>
      </div>
      <!-- count -->
      <div class="d-widget-shortcut-count" v-else-if="isListingCount">
        <div class="d-widget-shortcut-count-header">
          <fts-tippy
            append-content-class="d-widget-shortcut-count-tippy-content"
            class="d-widget-shortcut-count-value"
          >
            <template>{{abbreviateValue}}</template>
            <template #content>
              <div class="d-widget-shortcut-count-value">{{formatValue}}</div>
              <div
                class="d-widget-shortcut-count-footer"
                v-if="Boolean(item.aggregate) && Boolean(item.informationId)"
              >
                <div class="d-widget-shortcut-aggregate">
                  {{abbreviateAggregate}}
                </div>
                <fts-label-group
                  :item="{ 'type': item.informationType, 'displayName': item.displayName }"
                  information
                  class="d-widget-shortcut-information"
                  append-icon-class="d-widget-shortcut-information__icon"
                  append-label-class="d-widget-shortcut-information__label"
                  v-if="item.displayName"
                />
              </div>
            </template>
          </fts-tippy>
        </div>
        <div
          class="d-widget-shortcut-count-footer"
          v-if="Boolean(item.aggregate)"
        >
          <div class="d-widget-shortcut-aggregate" v-if="item.aggregate">
            {{abbreviateAggregate}}
          </div>
          <fts-label-group
            :item="{ 'type': item.informationType, 'displayName': item.displayName }"
            information
            class="d-widget-shortcut-information"
            append-icon-class="d-widget-shortcut-information__icon"
            v-if="item.displayName"
          />
        </div>
      </div>
    </div>
    <!-- error -->
    <div class="d-widget-overlay d-widget-overlay--error" v-if="isError">
      <!-- AutoCount Full's lock (402 connector_full_required): the listing's card, smaller -->
      <div class="d-widget-error fts-scrollbar fts-scrollbar-overlay" v-if="connectorLock">
        <fts-connector-lock-card :lock="connectorLock" compact />
      </div>
      <div class="d-widget-error fts-scrollbar fts-scrollbar-overlay" v-else>
        <div class="d-widget-error-title">
          {{error.title}}
        </div>
        <div class="d-widget-error-description">
          {{error.description}}
        </div>
      </div>
    </div>
    <!-- data exceed -->
    <div class="d-widget-overlay d-widget-overlay--exceed" v-if="isDataExceed">
      <div class="d-widget-error fts-scrollbar fts-scrollbar-overlay">
        <div class="d-widget-error-title">
          {{dataExceedWarn.title}}
        </div>
        <div class="d-widget-error-description">
          {{dataExceedWarn.description}}
        </div>
      </div>
    </div>
    <!-- action -->
    <fts-widget-action
      :show="showAction"
      class="stopPropagation"
      v-if="isListingCount || isConfigurable"
    >
      <!-- refresh -->
      <fts-btn
        text
        icon
        sm
        :disabled="loading"
        @click="refresh"
        v-if="isListingCount"
      >
        <font-awesome-icon :icon="['fas', 'sync-alt']" />
      </fts-btn>
      <template v-if="isConfigurable">
      <!-- configure -->
      <fts-btn
        text
        icon
        sm
        :disabled="loading"
        @click="configure"
      >
        <font-awesome-icon :icon="['fas', 'cog']" />
      </fts-btn>
      <!-- delete -->
      <fts-btn
        text
        icon
        sm
        :disabled="loading"
        @click="deleteConfirmation"
      >
        <font-awesome-icon :icon="['far', 'trash-alt']" />
      </fts-btn>
      </template>
    </fts-widget-action>
    <!-- load -->
    <div class="d-widget-overlay" v-if="loading">
      <fts-spinner show :size="24" />
    </div>
  </div>
</template>

<script>
import moment from 'moment';

import { authorizationStore } from '../../store/authorizationStore';
import { clientStore } from '../../store/clientStore';
import commons from '../../commons/commons';
import { responseErrorMessage, Widget_Error, widgetDataExceed } from '../../commons/message';

import FTSBtn from '../../elements/FTSBtn';
import FTSLabelGroup from '../../elements/FTSLabelGroup';
import FTSMoji from '../../elements/FTSMoji';
import FTSSpinner from '../../elements/FTSSpinner';
import FTSTippy from '../../elements/FTSTippy';
import FTSWidgetAction from '../FTSWidgetAction';
import FTSWidgetTitle from '../FTSWidgetTitle';
import FTSConnectorLockCard from '../../elements/FTSConnectorLockCard';
import { connectorLockFromError } from '../../elements/connectorLock';

export default {
  components: {
    "fts-connector-lock-card": FTSConnectorLockCard,
    "fts-btn": FTSBtn,
    "fts-label-group": FTSLabelGroup,
    "fts-moji": FTSMoji,
    "fts-spinner": FTSSpinner,
    "fts-tippy": FTSTippy,
    "fts-widget-action": FTSWidgetAction,
    "fts-widget-title": FTSWidgetTitle
  },
  props: {
    modules: Array,
    item: Object,
    filters: Object,
  },
  data: () => ({
    error: {
      title: Widget_Error.Title,
      description: Widget_Error.Description
    },
    loading: false,
    isError: false,
    // the widget's module is on AutoCount Full and this workspace is not (402)
    connectorLock: null,
    isDataExceed: false,
    showAction: false,
    abbreviateValue: '-',
    formatValue: '-'
  }),
  methods: {
    async refresh() {
      this.isError = false;
      this.connectorLock = null;
      if (!this.isCreate) {
        try {
          const findModule = this.modules.find((x) => x.id == this.item.moduleId);
          if (findModule) {
            const containListing = Array.isArray(findModule.menus) && findModule.menus.length;
            if (containListing && !this.item.listingId) {
              throw { errorException: 'configuration issue, missing Listing ID',};
            } else if (
              containListing && 
              this.item.listingId &&
              !findModule.menus.some((x) => x.id == this.item.listingId)
            ) {
              throw { errorException: 'Listing is not able to access or have been removed.' };
            }
          } else {
            throw { errorException: 'Module is not able to access or have been removed.' };
          }
        } catch(err) {
          console.error(err);
          this.error = {
            title: Widget_Error.Title,
            description: responseErrorMessage({
              message: (err && err.message) || Widget_Error.Description
            })
          };
          this.isError = true;
        }
      }
      if (!this.isError && this.isListingCount) {
        this.loading = true;
        try {
          const res = await clientStore.dispatch('retrieveWidget', { itemId: this.item.id, filters: this.filters });
          if (res) {
            let limit = 500;
            if (this.item.limit) {
              limit = this.item.limit;
            }

            let value = res.data.value;
            // external source data (AutoCount) is summarised in full in the source: no entry limit
            this.isDataExceed = !res.data.external && res.data.count > limit;
            if (!value && value != 0) {
              this.abbreviateValue = '-';
              this.formatValue = '-';
            } else {
              if (this.item.informationType === 5) {
                this.abbreviateValue = moment(value).format('ll');
                this.formatValue = moment(value).format('LL');
              } else if (this.item.informationType === 6) {
                this.abbreviateValue = moment(value).format('ll');
                this.formatValue = moment(value).format('LLL');
              } else {
                this.abbreviateValue = commons.formatNumber(
                  value,
                  {
                    currency: Boolean(this.item.informationType && this.item.informationType === 4),
                    abbreviate: true,
                  }
                );
                this.formatValue = commons.formatNumber(
                  value,
                  {
                    currency: Boolean(this.item.informationType && this.item.informationType === 4),
                  }
                );
              }
            }
          } else {
            throw 'error';
          }
        } catch(err) {
          console.error(err);
          this.error = {
            title: Widget_Error.Title,
            description: responseErrorMessage({
              error: err,
              message: Widget_Error.Description
            })
          };
          this.connectorLock = connectorLockFromError(err);
          this.isError = true;
        } finally {
          this.loading = false;
        }
      }
    },
    entryCreate() {
      this.$emit("entry:create", this.item.moduleId);
    },
    routeListing() {
      if (commons.elementStopPropagation()) return;

      this.$emit("route:listing", {
        moduleId: this.item.moduleId,
        listId: this.item.listingId
      });
    },
    configure() {
      this.$emit("configure", {
        refresh: this.refresh
      });
    },
    deleteConfirmation() {
      this.$modal.show("dialog", {
        title: "Confirmation",
        text: `Delete Shortcut?`,
        buttons: [
          { title: "Cancel"},
          {
            title: "Delete",
            default: true,
            handler: () => {
              this.$modal.hide("dialog");
              this.$emit("delete", this.item.id);
            },
          }
        ]
      })
    }
  },
  computed: {
    isConfigurable() {
      return authorizationStore.getters.isAdminUser;
    },
    isListingCount() {
      return this.item.type === 'listingWithCount';
    },
    isCreate() {
      return this.item.type === 'addNew';
    },
    informationType() {
      return commons.informationIcon(this.item.informationType);
    },
    abbreviateAggregate() {
      switch(this.item.aggregate) {
        case 'average':
          return 'avg';
        default:
          return this.item.aggregate;
      }
    },
    overlayEmoji() {
      const module = this.modules.find(x => x.id == this.item.moduleId);
      let emoji = null;
      if (this.item.listingId) {
        const listing = module && module.menus.find(x => x.id === this.item.listingId);
        if (listing && listing.emoji) {
          emoji = listing.emoji;
        }
      }
      if (!emoji && module && module.emoji) {
        emoji = module.emoji;
      }
      return emoji;
    },
    dataExceedWarn() {
      return widgetDataExceed(this.item && this.item.limit);
    }
  },
  mounted() {
    window.eventBus.$on('refreshShortcutWidget', this.refresh);
    this.refresh();
  },
  beforeDestroy() {
    window.eventBus.$off('refreshShortcutWidget', this.refresh);
  }
}
</script>

<style lang="scss" scoped>
@use "./src/components/dashboard/widgets/scss/style";
</style>

<style lang="scss">
@use "./src/assets/scss/vars" as *;

.d-widget-shortcut {
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 0.75rem;
  border-radius: 0.5rem;
  box-shadow: var(--fts-elevation-2);
  background-color: var(--fts-card-background);
  color: var(--fts-text);
  cursor: default;
  .d-widget-shortcut-overlay {
    @include absolute-center-x;
    @include user-select-none;
    @include pointer-events-none;
    bottom: 0.5rem;
    overflow: hidden;
    .d-widget-shortcut-overlay-emoji {
      opacity: 0.0875;
      filter: grayscale(25%);
    }
  }
  .d-widget-shortcut-header {
    height: 45px;
  }
  .d-widget-shortcut-footer {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    margin-top: 0.25rem;
  }
  .d-widget-shortcut-new {
    margin: auto;
  }
  .d-widget-shortcut-count-header {
    margin-bottom: 0.125rem;
    display: flex;
    align-items: baseline;
  }
  .d-widget-shortcut-count-value {
    font-weight: 500;
    font-size: 1.875rem;
    white-space: nowrap;
    overflow: hidden;
    cursor: default;
  }
}
.d-widget-shortcut-count-footer {
  display: flex;
  align-items: center;
}
.d-widget-shortcut-aggregate {
  margin-right: 0.375rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
}
.d-widget-shortcut-information {
  font-size: 0.875rem;
  line-height: 1.25rem;
  .d-widget-shortcut-information__icon {
    margin-right: 0.25rem;
  }
}
.d-widget-shortcut-count-tippy-content {
  .d-widget-shortcut-count-value {
    font-size: 0.875rem;
    & + .d-widget-shortcut-count-footer {
      margin-top: 0.375rem;
    }
  }
  .d-widget-shortcut-information {
    .d-widget-shortcut-information__label {
      color: var(--fts-light);
    }
  }
}
</style>